import { useEffect, useRef, useState } from 'react'
import { NotebookPen } from 'lucide-react'
import { NOTES_MAX } from '../constants/handoverConstants'

export interface SupervisorNotesProps {
  notes: string | null
  editable: boolean
  onSave?: (value: string) => void
  isSaving?: boolean
}

export function SupervisorNotes({ notes, editable, onSave, isSaving }: SupervisorNotesProps): JSX.Element {
  const [value, setValue] = useState(notes ?? '')
  const dirtyRef = useRef(false)
  const valueRef = useRef(value)
  const onSaveRef = useRef(onSave)
  valueRef.current = value
  onSaveRef.current = onSave

  useEffect(() => {
    if (!dirtyRef.current) setValue(notes ?? '')
  }, [notes])

  const flush = (): void => {
    if (!dirtyRef.current) return
    onSaveRef.current?.(valueRef.current)
    dirtyRef.current = false
  }

  useEffect(() => () => { flush() }, [])

  const handleChange = (next: string): void => {
    dirtyRef.current = true
    setValue(next.slice(0, NOTES_MAX))
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-5 w-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Supervisor Notes</h3>
        </div>
        {isSaving && <span className="text-xs text-gray-400">Saving…</span>}
      </div>

      {editable ? (
        <>
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={flush}
            maxLength={NOTES_MAX}
            rows={4}
            placeholder="Enter security notes, incident updates, or instructions for next shift…"
            className="w-full resize-y rounded-lg border border-gray-200 p-3 text-sm text-gray-700 focus:border-brand-green focus:outline-none"
          />
          <p className="mt-1 text-right text-xs text-gray-400">{value.length}/{NOTES_MAX}</p>
        </>
      ) : notes ? (
        <p className="whitespace-pre-wrap text-sm text-gray-700">{notes}</p>
      ) : (
        <p className="text-sm text-gray-400">No notes for this shift.</p>
      )}
    </div>
  )
}
